import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  UserCredential,
} from 'firebase/auth';
import {
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config/firebase';
import { getStudentDoc } from '../firebase/firestore/collections';
import {
  StudentProfile,
  StudentProfileCompletionInput,
  StaffAccount,
} from '../types';
import { LoginCredentials, RegisterCredentials } from './authTypes';
import { invalidateAdminStudentsCache } from '../services/adminBookingService';
import { sendStudentRegistrationNtfyNotification } from '../services/ntfyService';

/**
 * Service providing core Firebase Email + Password authentication
 * and synchronized Firestore profile management
 */
const ADMIN_PRESETS: Record<string, { username: string; displayName: string }> = {
  'wahdan2020@otostudents.com': {
    username: 'wahdan2020',
    displayName: 'وهدان (مشرف النظام)',
  },
  'whdan2026@otostudents.com': {
    username: 'whdan2026',
    displayName: 'وهدان (مشرف النظام)',
  },
  'nashgo2026@otostudents.com': {
    username: 'nashgo2026',
    displayName: 'إدارة نشجو (مشرف النظام)',
  },
  'omarkhaled20262@gmail.com': {
    username: 'omarkhaled',
    displayName: 'مدير المنظومة (عمر خالد)',
  },
  'admin@otostudents.com': {
    username: 'admin',
    displayName: 'إدارة OTO STUDENTS',
  },
};

export const authService = {
  /**
   * Register a new student using Email and Password
   * Automatically initializes student document in students/{uid} with profileCompleted = false
   */
  async register(credentials: RegisterCredentials): Promise<{ userCredential: UserCredential; profile: StudentProfile }> {
    const { fullName, phoneNumber, email, password } = credentials;
    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = (phoneNumber || '').trim();
    const isSpecialAdmin = !!ADMIN_PRESETS[cleanEmail];

    // 1. Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
    const uid = userCredential.user.uid;
    const nowIso = new Date().toISOString();

    // 2. Initialize student document in Firestore: students/{uid}
    const initialProfile: StudentProfile = {
      uid,
      fullName: isSpecialAdmin ? 'مدير المنظومة' : fullName.trim(),
      phoneNumber: cleanPhone,
      email: cleanEmail,
      address: isSpecialAdmin ? 'الإدارة المركزية - الإسكندرية' : '',
      universityGovernorate: isSpecialAdmin ? 'الإسكندرية' : (credentials.universityGovernorate || ''),
      universityId: isSpecialAdmin ? 'admin_hq' : (credentials.universityId || ''),
      universityName: isSpecialAdmin ? 'إدارة OTO STUDENTS' : (credentials.universityName || ''),
      status: 'ACTIVE',
      profileCompleted: isSpecialAdmin,
      createdAt: nowIso,
      updatedAt: nowIso,
      lastLoginAt: nowIso,
    };

    const studentRef = getStudentDoc(uid);
    await setDoc(studentRef, initialProfile);
    invalidateAdminStudentsCache();

    // Trigger real-time ntfy.sh push notification for new student account
    if (!isSpecialAdmin) {
      try {
        await sendStudentRegistrationNtfyNotification({
          uid,
          fullName: fullName.trim(),
          phoneNumber: cleanPhone,
          universityName: credentials.universityName || 'قيد الاستكمال',
          universityGovernorate: credentials.universityGovernorate || '',
          email: cleanEmail,
          createdAt: nowIso,
          source: 'SELF_REGISTER',
        });
      } catch (ntfyErr) {
        console.warn('[authService] Non-blocking registration ntfy notification failed:', ntfyErr);
      }
    }

    // If admin, initialize admin doc
    if (isSpecialAdmin) {
      try {
        const adminRef = doc(db, 'admins', uid);
        await setDoc(adminRef, {
          uid,
          email: cleanEmail,
          role: 'ADMIN',
          displayName: ADMIN_PRESETS[cleanEmail]?.displayName || 'مدير النظام',
          createdAt: nowIso,
          updatedAt: nowIso,
        }, { merge: true });
      } catch (err) {
        console.warn('Could not initialize admin doc in register:', err);
      }
    }

    return { userCredential, profile: initialProfile };
  },

  /**
   * Sign in existing user using Email/Username/Phone and Password
   * Handles:
   * 1. Phone number lookup (supports Egyptian 010..., 011..., 012..., 015...)
   * 2. Auto-bootstrap for admin accounts
   * 3. Auto-bootstrap for student accounts created by Admin in Firestore
   */
  async login(credentials: LoginCredentials): Promise<UserCredential> {
    const rawInput = credentials.email.trim();
    let cleanEmail = rawInput.toLowerCase();
    let studentDocSnapFromLookup: any = null;

    // A. If user entered a phone number or username (no '@')
    if (!rawInput.includes('@')) {
      const lower = rawInput.toLowerCase();
      if (lower === 'wahdan2020') {
        cleanEmail = 'wahdan2020@otostudents.com';
      } else if (lower === 'whdan2026') {
        cleanEmail = 'whdan2026@otostudents.com';
      } else if (lower === 'nashgo2026') {
        cleanEmail = 'nashgo2026@otostudents.com';
      } else if (lower === 'omarkhaled') {
        cleanEmail = 'omarkhaled20262@gmail.com';
      } else if (lower === 'admin') {
        cleanEmail = 'admin@otostudents.com';
      } else {
        const cleanPhone = rawInput.replace(/\D/g, '');
        // If it looks like a phone number (10 or 11 digits starting with 01 or 20)
        if (cleanPhone.length >= 10 && (cleanPhone.startsWith('01') || cleanPhone.startsWith('201') || cleanPhone.startsWith('1'))) {
          try {
            const phoneQ = query(collection(db, 'students'), where('phoneNumber', '==', rawInput));
            const phoneSnap = await getDocs(phoneQ);
            if (!phoneSnap.empty) {
              studentDocSnapFromLookup = phoneSnap.docs[0];
              cleanEmail = studentDocSnapFromLookup.data().email.toLowerCase();
            } else {
              cleanEmail = `${cleanPhone}@otostudents.com`;
            }
          } catch {
            cleanEmail = `${cleanPhone}@otostudents.com`;
          }
        } else {
          // It is a staff or admin username: maps directly to username@otostudents.com
          cleanEmail = `${lower}@otostudents.com`;
        }
      }
    }

    let userCredential: UserCredential;

    const presetAdmin = ADMIN_PRESETS[cleanEmail];
    const isSpecialAdmin = !!presetAdmin;

    try {
      userCredential = await signInWithEmailAndPassword(auth, cleanEmail, credentials.password);
      
      // Post-sign-in check: Ensure staff accounts are active and not SUSPENDED
      try {
        const adminDocRef = doc(db, 'admins', userCredential.user.uid);
        const adminDocSnap = await getDoc(adminDocRef);
        if (adminDocSnap.exists()) {
          const adminData = adminDocSnap.data();
          if (adminData.status === 'SUSPENDED') {
            await signOut(auth);
            throw new Error('تم تجميد هذا الحساب من قبل الإدارة العليا. يرجى مراجعة المسؤول.');
          }
          await updateDoc(adminDocRef, { lastLoginAt: new Date().toISOString() });
        }
      } catch (adminCheckErr: any) {
        if (adminCheckErr?.message?.includes('تم تجميد')) {
          throw adminCheckErr;
        }
      }
    } catch (err: any) {
      if (err?.message?.includes('تم تجميد')) {
        throw err;
      }

      // Cross-try for whdan / wahdan accounts if credential failed
      if (cleanEmail === 'whdan2026@otostudents.com' || cleanEmail === 'wahdan2020@otostudents.com') {
        const altEmail = cleanEmail === 'whdan2026@otostudents.com' ? 'wahdan2020@otostudents.com' : 'whdan2026@otostudents.com';
        try {
          userCredential = await signInWithEmailAndPassword(auth, altEmail, credentials.password);
          cleanEmail = altEmail;
          return userCredential;
        } catch (altErr) {
          // Continue to fallback
        }
      }
      // 1. If requested admin account does not exist yet in Firebase Auth, auto-create it immediately
      if (
        isSpecialAdmin &&
        (err?.code === 'auth/user-not-found' ||
          err?.code === 'auth/invalid-credential' ||
          err?.code === 'auth/invalid-login-credentials')
      ) {
        try {
          userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, credentials.password);
        } catch (createErr: any) {
          if (createErr?.code === 'auth/email-already-in-use') {
            throw err; // Password was wrong
          }
          throw createErr;
        }
      } else if (
        err?.code === 'auth/user-not-found' ||
        err?.code === 'auth/invalid-credential' ||
        err?.code === 'auth/invalid-login-credentials'
      ) {
        // 2. Check if this is an operational staff account in admins collection
        let matchedStaffDoc: any = null;
        try {
          const adminQ = query(collection(db, 'admins'), where('email', '==', cleanEmail));
          const adminSnap = await getDocs(adminQ);
          if (!adminSnap.empty) {
            matchedStaffDoc = adminSnap.docs[0];
          } else {
            // Check by ID if doc ID matches email username
            const docById = await getDoc(doc(db, 'admins', cleanEmail.split('@')[0]));
            if (docById.exists()) {
              matchedStaffDoc = docById;
            }
          }
        } catch (staffLookupErr) {
          console.warn('Staff Firestore lookup error:', staffLookupErr);
        }

        // Also check local backup for staff
        let localStaffMatch: any = null;
        if (!matchedStaffDoc && typeof window !== 'undefined') {
          try {
            const raw = localStorage.getItem('oto_dev_staff_accounts');
            if (raw) {
              const list = JSON.parse(raw);
              localStaffMatch = list.find((s: any) => s.email?.toLowerCase() === cleanEmail);
            }
          } catch {}
        }

        if (matchedStaffDoc || localStaffMatch) {
          const staffData = matchedStaffDoc ? matchedStaffDoc.data() : localStaffMatch;
          
          if (staffData.status === 'SUSPENDED') {
            throw new Error('تم تجميد هذا الحساب من قبل الإدارة العليا. يرجى مراجعة المسؤول.');
          }

          const expectedPass = staffData.password;
          if (expectedPass && expectedPass.trim() === credentials.password.trim()) {
            try {
              userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, credentials.password);
              const realUid = userCredential.user.uid;
              const nowIso = new Date().toISOString();

              const realAdminRef = doc(db, 'admins', realUid);
              await setDoc(realAdminRef, {
                ...staffData,
                uid: realUid,
                lastLoginAt: nowIso,
                updatedAt: nowIso,
              }, { merge: true });

              if (matchedStaffDoc && matchedStaffDoc.id !== realUid) {
                try {
                  await deleteDoc(matchedStaffDoc.ref);
                } catch {}
              }
            } catch (bootstrapErr: any) {
              if (bootstrapErr?.code === 'auth/email-already-in-use') {
                throw err;
              }
              throw bootstrapErr;
            }
          } else {
            throw err; // Invalid password
          }
        } else {
          // 3. If student account was created by Admin in Firestore with matching password
          let matchedStudentDoc = studentDocSnapFromLookup;
          if (!matchedStudentDoc) {
            try {
              const studentQ = query(collection(db, 'students'), where('email', '==', cleanEmail));
              const studentSnap = await getDocs(studentQ);
              if (!studentSnap.empty) {
                matchedStudentDoc = studentSnap.docs[0];
              }
            } catch (lookupErr) {
              console.warn('Student Firestore lookup error:', lookupErr);
            }
          }

          if (matchedStudentDoc) {
            const studentData = matchedStudentDoc.data();
            const expectedPass = studentData.password;

            // If password matches the record created by Admin
            if (expectedPass && expectedPass.trim() === credentials.password.trim()) {
              try {
                // Create user in Firebase Auth now
                userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, credentials.password);
                const realUid = userCredential.user.uid;
                const nowIso = new Date().toISOString();

                // Save to exact UID in Firestore
                const realStudentRef = doc(db, 'students', realUid);
                await setDoc(realStudentRef, {
                  ...studentData,
                  uid: realUid,
                  lastLoginAt: nowIso,
                  updatedAt: nowIso,
                }, { merge: true });

                // Remove temporary placeholder doc if UID was random
                if (matchedStudentDoc.id !== realUid) {
                  try {
                    await deleteDoc(matchedStudentDoc.ref);
                  } catch {
                    // Ignored
                  }
                }
              } catch (bootstrapErr: any) {
                if (bootstrapErr?.code === 'auth/email-already-in-use') {
                  throw err;
                }
                throw bootstrapErr;
              }
            } else {
              throw err; // Invalid password
            }
          } else {
            throw err;
          }
        }
      } else {
        throw err;
      }
    }
    
    // Update lastLoginAt timestamp & ensure records are fresh
    const uid = userCredential.user.uid;
    const nowIso = new Date().toISOString();
    const adminName = presetAdmin?.displayName || 'مدير المنظومة';
    const adminUsername = presetAdmin?.username || 'admin';

    try {
      const studentRef = getStudentDoc(uid);
      if (isSpecialAdmin) {
        await setDoc(
          studentRef,
          {
            uid,
            fullName: adminName,
            email: cleanEmail,
            address: 'الإدارة المركزية - الإسكندرية',
            universityGovernorate: 'الإسكندرية',
            universityId: 'admin_hq',
            universityName: 'إدارة OTO STUDENTS',
            status: 'ACTIVE',
            profileCompleted: true,
            lastLoginAt: nowIso,
          },
          { merge: true }
        );

        const adminRef = doc(db, 'admins', uid);
        await setDoc(
          adminRef,
          {
            uid,
            email: cleanEmail,
            username: adminUsername,
            role: 'ADMIN',
            displayName: adminName,
            updatedAt: nowIso,
          },
          { merge: true }
        );
      } else {
        await updateDoc(studentRef, {
          lastLoginAt: nowIso,
        });
      }
    } catch {
      // Ignore if document not yet created or permission delay
    }

    return userCredential;
  },

  /**
   * Sign out current student and clear Firebase session
   */
  async logout(): Promise<void> {
    await signOut(auth);
  },

  /**
   * Send Password Reset Email using standard Firebase Auth
   */
  async sendPasswordReset(email: string): Promise<void> {
    const cleanEmail = email.trim().toLowerCase();
    await sendPasswordResetEmail(auth, cleanEmail);
  },

  /**
   * Fetch student profile document from students/{uid}
   */
  async fetchProfile(uid: string): Promise<StudentProfile | null> {
    const studentRef = getStudentDoc(uid);
    const snap = await getDoc(studentRef);
    if (!snap.exists()) {
      return null;
    }
    return snap.data() as StudentProfile;
  },

  /**
   * Complete student profile with address, university, and optional contact phone
   * Cleanly omits any undefined values to avoid Firestore serialization errors
   */
  async completeProfile(uid: string, data: StudentProfileCompletionInput): Promise<StudentProfile> {
    const studentRef = getStudentDoc(uid);
    const nowIso = new Date().toISOString();

    const updatePayload: Partial<StudentProfile> = {
      fullName: data.fullName.trim(),
      address: data.address.trim(),
      universityGovernorate: data.universityGovernorate.trim(),
      universityId: data.universityId.trim(),
      profileCompleted: true,
      updatedAt: nowIso,
    };

    if (data.phoneNumber && data.phoneNumber.trim()) {
      updatePayload.phoneNumber = data.phoneNumber.trim();
    }
    if (data.universityName && data.universityName.trim()) {
      updatePayload.universityName = data.universityName.trim();
    }
    if (data.assignedRoute && data.assignedRoute.trim()) {
      updatePayload.assignedRoute = data.assignedRoute.trim();
    }
    if (data.defaultPickupStation && data.defaultPickupStation.trim()) {
      updatePayload.defaultPickupStation = data.defaultPickupStation.trim();
    }

    await setDoc(studentRef, updatePayload, { merge: true });

    // Return updated fresh profile
    const freshSnap = await getDoc(studentRef);
    const freshData = freshSnap.data() as StudentProfile;

    // Trigger ntfy.sh notification upon completing profile details
    try {
      await sendStudentRegistrationNtfyNotification({
        uid,
        fullName: freshData.fullName || data.fullName.trim(),
        phoneNumber: freshData.phoneNumber || data.phoneNumber?.trim() || '',
        universityName: freshData.universityName || data.universityName?.trim() || 'جامعة غير محددة',
        universityGovernorate: freshData.universityGovernorate || data.universityGovernorate.trim(),
        address: freshData.address || data.address.trim(),
        email: freshData.email || '',
        assignedRoute: freshData.assignedRoute || data.assignedRoute?.trim(),
        defaultPickupStation: freshData.defaultPickupStation || data.defaultPickupStation?.trim(),
        createdAt: freshData.createdAt || nowIso,
        source: 'PROFILE_COMPLETED',
      });
    } catch (ntfyErr) {
      console.warn('[authService] Non-blocking completeProfile ntfy dispatch failed:', ntfyErr);
    }

    return freshData;
  },

  /**
   * Fetch staff account profile from admins/{uid} or email search or local backup
   */
  async fetchStaffProfile(uid: string, email?: string | null): Promise<StaffAccount | null> {
    try {
      // 1. Direct lookup by Firebase Auth UID (fastest & standard)
      const adminRef = doc(db, 'admins', uid);
      const snap = await getDoc(adminRef);
      if (snap.exists()) {
        return snap.data() as StaffAccount;
      }

      // 2. Fallback lookup by email in admins collection
      const targetEmail = (email || auth.currentUser?.email || '').trim().toLowerCase();
      if (targetEmail) {
        try {
          const q = query(collection(db, 'admins'), where('email', '==', targetEmail), limit(1));
          const qSnap = await getDocs(q);
          if (!qSnap.empty) {
            const foundDoc = qSnap.docs[0];
            const data = foundDoc.data() as StaffAccount;
            // Auto-heal: Ensure doc also exists at admins/{uid} so future reads are instantaneous
            try {
              await setDoc(doc(db, 'admins', uid), { ...data, uid }, { merge: true });
            } catch {}
            return { ...data, uid };
          }
        } catch {}
      }

      // 3. Fallback: If username matches doc ID
      if (targetEmail && targetEmail.endsWith('@otostudents.com')) {
        const username = targetEmail.split('@')[0];
        try {
          const usernameDoc = await getDoc(doc(db, 'admins', username));
          if (usernameDoc.exists()) {
            const data = usernameDoc.data() as StaffAccount;
            try {
              await setDoc(doc(db, 'admins', uid), { ...data, uid }, { merge: true });
            } catch {}
            return { ...data, uid };
          }
        } catch {}
      }

      // 4. Fallback: Local dev backup
      if (typeof window !== 'undefined') {
        const raw = localStorage.getItem('oto_dev_staff_accounts');
        if (raw) {
          const list: StaffAccount[] = JSON.parse(raw);
          const found = list.find((s) => s.uid === uid || (targetEmail && s.email?.toLowerCase() === targetEmail));
          if (found) return found;
        }
      }
    } catch (err) {
      console.warn('Error fetching staff profile:', err);
    }
    return null;
  },
};
